// src/components/page/article/ArticleDetailModal.tsx
'use client';

import * as React from 'react';
import ModalArticle from '@/components/ui/modal/ArticleModal';
import HeaderContent from '@/components/header/article/HeaderContent';
import ArticleDetail from '@/components/page/article/ArticleDetail';
import type { Article } from '@/model/article';
import { ArticleService } from '@/libs/services/article';
import type { ArticleDetailParams } from '@/libs/services/article';

/**
 * 將 slug（例如：collection_682 / store_article_123）轉回 ArticleDetailParams
 * 規則：
 *  - 最後一段當作 table_id
 *  - 前面所有段落 join('_') 當作 table
 *   例如：
 *    - "collection_682"     → table: "collection",     table_id: 682
 *    - "store_article_123"  → table: "store_article",  table_id: 123
 */
function slugToDetailParams(slug: string): ArticleDetailParams | null {
  if (!slug) return null;

  const parts = slug.split('_');
  if (parts.length < 2) return null;

  const idPart = parts[parts.length - 1];
  const table = parts.slice(0, -1).join('_');

  if (!table || !idPart) return null;

  const num = Number(idPart);
  const table_id = Number.isNaN(num) ? idPart : num;

  return { table, table_id };
}

/** 依 bizType 決定是否顯示「領取 / 互動」區塊 */
export function shouldShowClaimActions(article: Article): boolean {
  const interactiveTypes: Article['bizType'][] = [
    'mood',
    'message_get_coin',
    'coin_give',
    'coin_receive',
    'coin_exchange',
    'secret',
    'pair',
    'lottery',
    'kol_privilege',
    'privilege',
  ];

  return interactiveTypes.includes(article.bizType);
}

export type ArticleDetailModalProps = {
  /** 是否開啟 modal */
  open: boolean;
  /**
   * 文章 ID（slug，例如：collection_682）
   * - CoinCanvas 這類只有 slug 的地方 → 用這個打 API
   * - 路由 modal 也可以直接帶進來，保持一致
   */
  articleId: string;
  /**
   * 若外部已經有完整 Article（例如 server 預先取得），可以直接傳進來：
   * - 有給時 → 不會再打 API
   * - 沒給時 → 會用 articleId（slug）去呼叫 ArticleService.detail
   */
  article?: Article;
  /** 關閉 modal 時呼叫 */
  onClose: () => void;
  /** Modal 寬度，可自行覆寫 */
  width?: string;
  /** 強制指定是否顯示「領取 / 互動」區塊；不給就用預設判斷 */
  showClaimActionsOverride?: boolean;
  /** 是否顯示底部回覆/互動列（預設 true） */
  showReplyBar?: boolean;
};

export default function ArticleDetailModal({
  open,
  article,
  articleId,
  onClose,
  width = 'min(100vw, 700px)',
  showClaimActionsOverride,
  showReplyBar = true,
}: ArticleDetailModalProps) {
  const [internalArticle, setInternalArticle] = React.useState<Article | null>(article ?? null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // 當外部 article 改變時，內部也同步更新
  React.useEffect(() => {
    if (article) {
      setInternalArticle(article);
      setError(null);
      setLoading(false);
    }
  }, [article]);

  React.useEffect(() => {
    // 沒開的時候不用打 API
    if (!open) return;

    // 若已經有外部傳進來的 article，就不用再打 API
    if (article) return;

    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError(null);

        const params = slugToDetailParams(articleId);

        if (!params) {
          if (!cancelled) {
            setError('無效的文章 ID');
            setInternalArticle(null);
          }
          return;
        }

        const data = await ArticleService.detail(params);

        if (!cancelled) {
          setInternalArticle(data);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[ArticleDetailModal] Failed to load article detail', err);
        if (!cancelled) setError('載入文章時發生錯誤');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [open, article, articleId]);

  const resolvedArticle = article ?? internalArticle;

  const showClaimActions = React.useMemo(() => {
    if (typeof showClaimActionsOverride === 'boolean') {
      return showClaimActionsOverride;
    }
    if (!resolvedArticle) return false;
    return shouldShowClaimActions(resolvedArticle);
  }, [resolvedArticle, showClaimActionsOverride]);

  // ArticleDetail 內部需要的 articleId，我們統一使用 slug（路由/CloudCoin 都是這個格式）
  const effectiveArticleId = articleId;

  const header = resolvedArticle ? (
    <HeaderContent
      // 保持跟你原本 ModalClient 一樣用 article.id 傳給 HeaderContent
      articleId={resolvedArticle.id}
      article={resolvedArticle}
      sourceLabel="modal-title"
      onClose={onClose}
    />
  ) : (
    '文章載入中…'
  );

  return (
    <ModalArticle open={open} onClose={onClose} title={header} width={width}>
      {/* 載入中但還沒有文章資料 */}
      {loading && !resolvedArticle && (
        <div className="py-5 text-center text-muted">文章載入中…</div>
      )}

      {/* 發生錯誤且沒有文章資料 */}
      {error && !resolvedArticle && <div className="py-5 text-center text-danger">{error}</div>}

      {/* 有文章資料才渲染 ArticleDetail */}
      {resolvedArticle && (
        <ArticleDetail
          articleId={effectiveArticleId}
          article={resolvedArticle}
          showDesktopHeader={false} // modal 內不要再顯示桌機 header
          showReplyBar={showReplyBar}
          showClaimActions={showClaimActions}
        />
      )}
    </ModalArticle>
  );
}
